import React, { useEffect, useState, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Box,
  Typography,
  Badge,
  Tooltip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { fetchChatCategories } from '../http/categoryAPI';
import { getCategoriesForAdmin } from '../http/adminChatCategoryAccessAPI';
import { Context } from '../index';

const ChatCategoryMenu = observer(({ anchorEl, open, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, chatStats } = useContext(Context);

  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      // Проверяем доступ к чатам вообще
      if (!user.user?.access?.chat && !user.user?.access?.isOwner) {
        setCategories([]);
        setLoading(false);
        return;
      }

      // Владельцы видят все категории
      if (user.user?.access?.isOwner) {
        const data = await fetchChatCategories();
        setCategories(data);
        if (user.user?.id) {
          await Promise.all(
            (data || []).map((category) =>
              Promise.all([
                chatStats.loadClosedStats(user.user.id, category.id),
                chatStats.loadOpenedStats(user.user.id, category.id),
                chatStats.loadMyStats(user.user.id, category.id),
              ])
            )
          );
        }
      } else {
        // Обычные админы видят только те, к которым есть доступ
        const data = await getCategoriesForAdmin(user.user.id);
        setCategories(data || []);
        if (user.user?.id) {
          await Promise.all(
            (data || []).map((category) =>
              Promise.all([
                chatStats.loadClosedStats(user.user.id, category.id),
                chatStats.loadOpenedStats(user.user.id, category.id),
                chatStats.loadMyStats(user.user.id, category.id),
              ])
            )
          );
        }
      }
    } catch (err) {
      console.error('Ошибка при загрузке категорий:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/chats?category=${categoryId}`);
    onClose();
  };

  const handleSettingsClick = () => {
    navigate('/chats/settings');
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          minWidth: 220,
        },
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Выберите категорию чатов
            </Typography>
          </Box>
          {categories.map((category) => (
            <MenuItem
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
            >
              <ListItemIcon>
                <ChatIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>{category.name}</span>
                      {category.isDefault && (
                        <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                          (по умолчанию)
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {chatStats.getClosedStats(category.id)?.unreadCount > 0 && (
                        <Tooltip title="Непрочитанные чаты" arrow>
                          <Badge
                            badgeContent={chatStats.getClosedStats(category.id).unreadCount}
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                position: 'static',
                                transform: 'none',
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px',
                                padding: '0 4px',
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {chatStats.getClosedStats(category.id)?.requestsCount > 0 && (
                        <Tooltip title="Необработанные заявки" arrow>
                          <Badge
                            badgeContent={chatStats.getClosedStats(category.id).requestsCount}
                            color="error"
                            sx={{
                              '& .MuiBadge-badge': {
                                position: 'static',
                                transform: 'none',
                                fontSize: '0.7rem',
                                height: '18px',
                                minWidth: '18px',
                                padding: '0 4px',
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                }
              />
            </MenuItem>
          ))}
          
          {categories.length === 0 && !loading && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Нет категорий
              </Typography>
            </Box>
          )}
          
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleSettingsClick}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Настройка категорий</ListItemText>
          </MenuItem>
        </>
      )}
    </Menu>
  );
});

export default ChatCategoryMenu;
